import type { ReactNode } from "react";
import { Loader2, ChevronRight, Activity, FileText } from "lucide-react";

export interface ActivityItem {
    label: string;
    value: string;
    status?: string;
    statusColor?: string;
    icon?: ReactNode;
    onClick?: () => void;
}

interface ActivityCardProps {
    title: string;
    items: ActivityItem[];
    loading?: boolean;
    emptyMessage?: string;
    viewAllLink?: string;
    onViewAll?: () => void;
}

const ActivityCard = ({
    title,
    items,
    loading = false,
    emptyMessage = "No items found",
    onViewAll,
}: ActivityCardProps) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {onViewAll && (
                <button
                    onClick={onViewAll}
                    className="text-xs text-[#1a7a4a] font-medium flex items-center gap-1 hover:underline"
                >
                    View all <ChevronRight className="h-3 w-3" />
                </button>
            )}
        </div>

        {loading ? (
            <div className="flex justify-center items-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-[#1a7a4a]" />
            </div>
        ) : (
            <ul className="space-y-2">
                {items.length > 0 ? (
                    items.map((item, idx) => (
                        <li
                            key={idx}
                            className={`flex items-center justify-between py-2.5 px-3 rounded-xl transition-colors ${item.onClick ? "hover:bg-gray-50 cursor-pointer" : ""
                                }`}
                            onClick={item.onClick}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-8 w-8 rounded-full bg-[#e8f5ee] flex items-center justify-center flex-shrink-0">
                                    {item.icon || <Activity className="h-3.5 w-3.5 text-[#1a7a4a]" />}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-800 truncate">
                                        {item.label}
                                    </div>
                                    <div className="text-xs text-gray-400 truncate">
                                        {item.value}
                                    </div>
                                </div>
                            </div>
                            {item.status && (
                                <span
                                    className={`ml-3 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${item.statusColor || "bg-gray-100 text-gray-600"}`}
                                >
                                    {item.status}
                                </span>
                            )}
                        </li>
                    ))
                ) : (
                    <div className="text-center py-10">
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                            <FileText className="h-5 w-5 text-gray-300" />
                        </div>
                        <div className="text-sm text-gray-400">{emptyMessage}</div>
                    </div>
                )}
            </ul>
        )}
    </div>
);

export default ActivityCard;
